interface IDNAttribute {
  key: string;
  value: string;
}

const FALLBACK_KEYS = ['O', 'CN', 'OU'] as const;

/**
 * Estrae un nome leggibile dell'ente certificatore da un DN X.500/RFC 4514.
 * Ordine di fallback: O → CN → OU → primo valore plain → stringa originale.
 * Ignora i valori hex-encoded (es. "#0c11...") non human-readable.
 */
export function extractCertificateAuthority(
  dn: string | null | undefined,
): string {
  if (!dn) return '';
  const trimmed = dn.trim();
  if (!trimmed) return '';

  const attributes = parseDN(trimmed);
  if (attributes.length === 0) return trimmed;

  for (const key of FALLBACK_KEYS) {
    const found = attributes.find(a => a.key === key && a.value);
    if (found) return found.value;
  }

  const firstUsable = attributes.find(a => a.value);
  return firstUsable ? firstUsable.value : trimmed;
}

function parseDN(dn: string): IDNAttribute[] {
  const result: IDNAttribute[] = [];
  let cursor = 0;

  while (cursor < dn.length) {
    cursor = skipSeparators(dn, cursor);
    if (cursor >= dn.length) break;

    const keyEnd = dn.indexOf('=', cursor);
    if (keyEnd === -1) break;

    const key = dn.slice(cursor, keyEnd).trim().toUpperCase();
    cursor = keyEnd + 1;

    const { value, next } = readValue(dn, cursor);
    cursor = next;
    result.push({ key, value });
  }

  return result;
}

function skipSeparators(dn: string, start: number): number {
  let i = start;
  while (i < dn.length && (dn[i] === ' ' || dn[i] === ',')) i++;
  return i;
}

function readValue(dn: string, start: number): { value: string; next: number } {
  if (dn[start] === '#') return readHexValue(dn, start);
  return readPlainValue(dn, start);
}

function readHexValue(
  dn: string,
  start: number,
): { value: string; next: number } {
  let i = start;
  while (i < dn.length && dn[i] !== ',') i++;
  return { value: '', next: i };
}

function readPlainValue(
  dn: string,
  start: number,
): { value: string; next: number } {
  let value = '';
  let i = start;
  while (i < dn.length) {
    const ch = dn[i];
    if (ch === '\\' && i + 1 < dn.length) {
      value += dn[i + 1];
      i += 2;
      continue;
    }
    if (ch === ',') break;
    value += ch;
    i++;
  }
  return { value: value.trim(), next: i };
}
